import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  GROUP_TYPE_REDSHIFT_MARKETPLACE_DATA_PERMISSION_BASELINE_ONLY,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { getGroupInfo, getGroupMembers, getWorkspace } from 'src/api/permissions';
import {
  GetGroupMembersResult,
  GetGroupResult,
  GetWorkspaceResult,
  RoleMappingMap,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import {
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Flashbar,
  Header,
  Spinner,
  Wizard,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { EmptyState } from 'src/commons/EmptyState';
import { isValidWorkspace } from 'src/commons/validationUtils';
import {
  MembershipMap,
  Reason,
  UserMembershipModal,
} from 'src/components/permissions/myBaselining/userMembershipModal';
import { createOwnerLink, Page } from 'src/routes';
import { getWorkspaceDisplayName } from 'src/commons/common';
import { BaselineOmniGrants } from 'src/components/permissions/myBaselining/baselineOmniGrants';
import {
  BaselineQuestionnaireForm,
  QuestionnaireData,
} from 'src/components/permissions/myBaselining/baselineQuestionnaireForm';
import { BaselineReview } from 'src/components/permissions/myBaselining/baselineReview';
import {
  submitBaselineRevoke,
  submitBaselineWithQuestionnaire,
} from 'src/components/permissions/myBaselining/baseliningUtils';
import { scrollUp } from 'src/components/utils/navigation';
import { ManageRoles } from 'src/components/groups/manageRoles';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface BaselineMembershipProps extends CommonPageProps {
  catalogMap: any;
  reviewUsers: boolean;
  reviewPermissions: boolean;
  userInfo: any;
}

export const BaselineOwner = (props: BaselineMembershipProps) => {
  const [loadingBaseline, setLoadingBaseline] = useState(true);
  const [groupInfo, setGroupInfo] = useState<GetGroupResult | undefined>(undefined);
  const [workspace, setWorkspace] = useState<GetWorkspaceResult | undefined>(undefined);
  const [groupMembers, setGroupMembers] = useState<GetGroupMembersResult | undefined>(undefined);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const ownerId = props.match.params.ownerId;
  const dataAccessPermissionBaselineOnlyTypes = [GROUP_TYPE_REDSHIFT_MARKETPLACE_DATA_PERMISSION_BASELINE_ONLY];
  const [dataPermissions, setDataPermissions] = useState([]);
  const [dpToKeep, setDpToKeep] = useState([]);
  const [dpToRevoke, setDpToRevoke] = useState([]);
  const [questionnaireList, setQuestionnaireList] = useState<QuestionnaireData[]>([]);
  const [notifications, setNotifications] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [allTermsAccepted, setAllTermsAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [props.match.params.ownerId]);

  const handleRefresh = async () => {
    try {
      setLoadingBaseline(true);
      if (!ownerId) return;
      let groupId = ownerId;

      if (isValidWorkspace(ownerId)) {
        let getWorkspaceResponse = await getWorkspace({
          workspaceId: ownerId,
        });
        groupId = getWorkspaceResponse?.workspace?.groupId;
        setWorkspace(getWorkspaceResponse);
      }

      const groupPromise = getGroupInfo({
        groupId: groupId,
      });
      const groupMembersPromise = getGroupMembers({
        groupId: groupId,
      });
      const [group, groupMembers] = await Promise.all([groupPromise, groupMembersPromise]);

      setGroupMembers(groupMembers);
      setGroupInfo(group);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBaseline(false);
    }
  };

  const getHumanMembers = () => {
    const humanMembers = groupMembers?.groupMembers
      ?.filter(
        (user) =>
          (user?.memberWorkspaceIds?.includes(ownerId) || user?.memberGroupIds?.includes(ownerId)) &&
          user?.userId?.indexOf(':') === -1,
      )
      .map((user) => user.userId)
      .sort()
      .map((user) => (
        <div key={user}>
          {
            <Button
              variant='inline-link'
              iconName='user-profile-active'
              onClick={() => {
                setSelectedUser(user);
                setUserModalVisible(true);
              }}
            >
              {user}
            </Button>
          }
        </div>
      ));
    return humanMembers?.length ? (
      <ColumnLayout borders='all' columns={4}>
        {humanMembers}
      </ColumnLayout>
    ) : (
      <EmptyState subtitle={'No human users found'} title={'No human users'} />
    );
  };

  let groupings: MembershipMap = {};
  let targetRoleMapping: RoleMappingMap;
  let additionalReasons: Reason[] = [];

  const isValidForm = () => {
    let valid = true;
    if (props.reviewPermissions && !allTermsAccepted) {
      setNotifications([
        {
          type: 'error',
          content: `Please accept the terms and conditions`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
      valid = false;
    }
    if (submitted) {
      setNotifications([
        {
          type: 'error',
          content: `Access review is already submitted.`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
      valid = false;
    }
    return valid;
  };

  const handleSubmitBaseline = async () => {
    setButtonLoading(true);
    try {
      if (!isValidForm()) {
        setButtonLoading(false);
        scrollUp();
        return;
      }
      if (props.reviewUsers && !props.reviewPermissions) {
        props.setRedirect(Page.BASELINING);
        return;
      }
      await Promise.all([submitBaselineWithQuestionnaire(questionnaireList), submitBaselineRevoke(dpToRevoke)]);

      setSubmitted(true);
      setNotifications([
        {
          type: 'success',
          content: `Successfully submitted baseline requests`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } catch (e) {
      console.log(e);
      setButtonLoading(false);
      // TO DO with error handling from backend
      setNotifications([
        {
          type: 'error',
          content: `Failed to submit baseline requests`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    scrollUp();
    setButtonLoading(false);
  };

  const addOrUpdate = (obj: MembershipMap, key: string, val: Reason) => {
    if (key in obj) {
      obj[key].push(val);
    } else {
      obj[key] = [val];
    }
  };

  const addRoleMembership = (mapping: RoleMappingMap, ownerId: string) => {
    if (mapping) {
      for (const [name, role] of Object.entries(mapping)) {
        if (role?.groupingMechanismIds) {
          role.groupingMechanismIds.forEach((grp) =>
            addOrUpdate(
              groupings,
              grp,
              <>
                Is associated to Omni role <b>{name.split('#').pop()}</b> of {ownerId}
              </>,
            ),
          );
        }
        if (role?.roleMembers && role.roleMembers.includes(selectedUser)) {
          additionalReasons.push(
            <>
              Is added directly to Omni role <b>{name.split('#').pop()}</b> of {ownerId}
            </>,
          );
        }
      }
    }
  };

  if (isValidWorkspace(ownerId)) {
    if (workspace?.workspace?.owner) {
      addOrUpdate(
        groupings,
        workspace.workspace.owner,
        <>
          Is owner of workspace{' '}
          {
            <Link external={true} href={createOwnerLink(ownerId)}>
              {getWorkspaceDisplayName(workspace.workspace.workspaceName, ownerId)}
            </Link>
          }
        </>,
      );
    }
    targetRoleMapping = workspace?.workspace?.roleMapping;
    addRoleMembership(targetRoleMapping, ownerId);
  } else {
    // group
    targetRoleMapping = groupInfo?.roleMapping;
  }

  if (groupInfo?.groupingMechanismId) {
    addOrUpdate(
      groupings,
      groupInfo.groupingMechanismId,
      <>
        Is owner of group{' '}
        {
          <Link external={true} href={createOwnerLink(groupInfo.groupId)}>
            {groupInfo.groupId}
          </Link>
        }
      </>,
    );
    addRoleMembership(groupInfo.roleMapping, groupInfo.groupId);
  }

  const defaultWizardSteps = [];
  if (props.reviewUsers) {
    defaultWizardSteps.push(
      {
        title: 'Review users',
        info: <Link variant='info'>Info</Link>,
        description:
          'Review the list of users that have access to your group/workspace. If you see any user that should not have access, you can remove it in the next steps',
        content: (
          <ContentLayout
            header={
              <Container
                header={
                  <Header
                    variant='h2'
                    description='This is the list of human users that have access to at least one of your Omni roles or POSIX/Teams.'
                  >
                    Human users with access to {ownerId}
                  </Header>
                }
              >
                {loadingBaseline ? <Spinner /> : getHumanMembers()}
              </Container>
            }
          ></ContentLayout>
        ),
      },
      {
        title: 'Review Omni roles',
        description:
          'Review the Omni roles from this group/workspace. You can edit individual roles to remove users/groups.',
        content: (
          <ManageRoles
            ownerId={ownerId}
            roleMapping={targetRoleMapping}
            allowAdd={false}
            allowEdit={false}
            headerText={`Baseline Omni roles for ${ownerId}`}
            description={
              'Review that the right people and IAM principals have the right access for each of the Omni roles. Click on each of the roles and remove any people that should no longer have access. ' +
              'For each of the grouping mechanisms (POSIX, Teams), click on the link and review that the right people belong to the POSIX/Team.'
            }
          />
        ),
      },
    );
  }

  if (props.reviewPermissions) {
    defaultWizardSteps.push(
      {
        title: 'Review data access grants',
        description:
          'Deselect any datasets you no longer require access to. Your permissions will remain active for all selected datasets.',
        content: (
          <BaselineOmniGrants
            {...props}
            dataPermissions={dataPermissions}
            setDataPermissions={setDataPermissions}
            setDpToKeep={setDpToKeep}
            setDpToRevoke={setDpToRevoke}
          />
        ),
      },
      {
        title: 'Review questionnaire',
        description: 'Verify all information below and update as necessary.',
        content: (
          <BaselineQuestionnaireForm
            {...props}
            dataPermissionList={dpToKeep}
            questionnaireList={questionnaireList}
            setQuestionnaireList={setQuestionnaireList}
          />
        ),
      },
      {
        title: 'Summary',
        description: 'The following changes will be made when this form is submitted.',
        content: (
          <BaselineReview
            {...props}
            dpToKeep={dpToKeep}
            dpToRelinquish={dpToRevoke}
            allTermsAccepted={allTermsAccepted}
            setAllTermsAccepted={setAllTermsAccepted}
          />
        ),
      },
    );
  }

  const getWizardSteps = () => {
    // Only returns the data access permission baseline step for baseline-only groups created and managed by Omni
    if (!isValidWorkspace(ownerId) && dataAccessPermissionBaselineOnlyTypes.includes(groupInfo?.config?.groupType)) {
      return defaultWizardSteps.filter((step) => step.title === 'Review data access permissions');
    }
    return defaultWizardSteps;
  };

  return (
    <>
      <Flashbar items={notifications}></Flashbar>
      <UserMembershipModal
        onClose={() => {
          setUserModalVisible(false);
        }}
        userId={selectedUser}
        visible={userModalVisible}
        membership={groupings}
        additionalReasons={additionalReasons}
      />
      <Wizard
        i18nStrings={{
          stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
          navigationAriaLabel: 'Steps',
          cancelButton: 'Cancel',
          previousButton: 'Previous',
          nextButton: 'Next',
          optional: 'optional',
        }}
        onSubmit={handleSubmitBaseline}
        submitButtonText='Confirm and submit'
        onNavigate={({ detail }) => {
          if (dpToKeep.length == 0 && dpToRevoke.length == 0) {
            if (
              (props.reviewUsers && detail.requestedStepIndex == 3) ||
              (!props.reviewUsers && props.reviewPermissions && detail.requestedStepIndex == 1)
            ) {
              setNotifications([
                {
                  type: 'info',
                  content: `There are no access grants to review`,
                  dismissible: true,
                  onDismiss: () => setNotifications([]),
                },
              ]);
              return;
            }
          }
          setActiveStepIndex(detail.requestedStepIndex);
        }}
        activeStepIndex={activeStepIndex}
        allowSkipTo
        steps={getWizardSteps()}
        isLoadingNextStep={buttonLoading}
      />
    </>
  );
};
